import { WSQueryCache } from "@ws-react-query";
import { Transaction, TransactionUpdate } from "@wingspanhq/wingspan-api-sdk";
import { transactionsApi } from "@api";
import { queriesProfitAndLoss, queriesTransactions } from "@query";
import { bulkActionFactory, useForegroundBulkAction } from "../../BulkActions";
import { useModalBulkRenameTransactions } from "../modals/ModalBulkRenameTransactions";
import { useModalBulkSetAmount } from "../modals/ModalBulkSetAmount";
import { useModalBulkSetType } from "../modals/ModalBulkSetType";
import { useModalBulkUpdateCategory } from "../modals/ModalBulkUpdateCategory";

const invalidateAfterBulk = () => {
  void WSQueryCache.invalidateQueries(
    queriesTransactions.listTransactions.queryKeyBase
  );
  void WSQueryCache.invalidateQueries(
    queriesProfitAndLoss.getProfitAndLoss.queryKeyBase
  );
};

function useRunBulkUpdate() {
  const runForegroundBulkAction = useForegroundBulkAction();

  return (config: {
    actionName: string;
    transactions: Transaction[];
    update: TransactionUpdate;
    skipConfirm?: boolean;
    confirmDescription?: string;
    confirmLabel?: string;
    confirmDestructive?: boolean;
  }) =>
    runForegroundBulkAction({
      actionName: config.actionName,
      itemCount: config.transactions.length,
      skipConfirm: config.skipConfirm,
      confirmDescription: config.confirmDescription,
      confirmLabel: config.confirmLabel,
      confirmDestructive: config.confirmDestructive,
      prepare: async () => config.transactions,
      process: async (items, onProgress) => {
        const bulk = bulkActionFactory<Transaction>({
          onProgress,
          action: async transaction => {
            await transactionsApi.updateTransaction({
              transactionId: transaction.id,
              transactionUpdate: config.update
            });
          },
          onFinish: invalidateAfterBulk
        });
        const { successItems, failedItems } = await bulk.run(items);
        return {
          total: items.length,
          processed: successItems.length + failedItems.length,
          successful: successItems.length,
          failed: failedItems.length
        };
      }
    });
}

export function useBulkRenameTransactions() {
  const modalRename = useModalBulkRenameTransactions();
  const runBulkUpdate = useRunBulkUpdate();

  return async (transactions: Transaction[]) => {
    const name = await modalRename.open({ count: transactions.length });
    if (!name) return false;
    await runBulkUpdate({
      actionName: "Rename transactions",
      transactions,
      update: { name },
      skipConfirm: true
    });
    return true;
  };
}

export function useBulkSetAmountTransactions() {
  const modalSetAmount = useModalBulkSetAmount();
  const runBulkUpdate = useRunBulkUpdate();

  return async (transactions: Transaction[]) => {
    const amount = await modalSetAmount.open({ count: transactions.length });
    if (amount == null) return false;
    await runBulkUpdate({
      actionName: "Set amount",
      transactions,
      update: { amount },
      skipConfirm: true
    });
    return true;
  };
}

export function useBulkSetTypeTransactions() {
  const modalSetType = useModalBulkSetType();
  const runBulkUpdate = useRunBulkUpdate();

  return async (transactions: Transaction[]) => {
    const type = await modalSetType.open({ count: transactions.length });
    if (type == null) return false;
    await runBulkUpdate({
      actionName: "Set type",
      transactions,
      update: { type },
      skipConfirm: true
    });
    return true;
  };
}

export function useBulkUpdateCategoryTransactions() {
  const modalCategory = useModalBulkUpdateCategory();
  const runBulkUpdate = useRunBulkUpdate();

  return async (transactions: Transaction[]) => {
    const category = await modalCategory.open({ count: transactions.length });
    if (!category?.wsCategory) return false;
    await runBulkUpdate({
      actionName: "Update category",
      transactions,
      update: {
        wsCategory: category.wsCategory,
        subcategory: category.subcategory || null
      },
      skipConfirm: true
    });
    return true;
  };
}

export function useBulkSetBusinessTransactions() {
  const runBulkUpdate = useRunBulkUpdate();

  return async (transactions: Transaction[], isBusiness: boolean) => {
    await runBulkUpdate({
      actionName: isBusiness
        ? "Mark as tax-deductible"
        : "Mark as not deductible",
      transactions,
      update: { isBusiness },
      confirmDescription: isBusiness
        ? `Mark ${transactions.length} transactions as tax-deductible?`
        : `Mark ${transactions.length} transactions as not deductible?`
    });
    return true;
  };
}

export function useBulkDeleteTransactions() {
  const runForegroundBulkAction = useForegroundBulkAction();

  return async (transactions: Transaction[]) => {
    await runForegroundBulkAction({
      actionName: "Delete transactions",
      itemCount: transactions.length,
      confirmDescription: `Permanently delete ${transactions.length} transactions? This can't be undone.`,
      confirmLabel: "Delete",
      confirmDestructive: true,
      prepare: async () => transactions,
      process: async (items, onProgress) => {
        const bulk = bulkActionFactory<Transaction>({
          onProgress,
          action: async transaction => {
            await transactionsApi.deleteTransaction({
              transactionId: transaction.id
            });
          },
          onFinish: invalidateAfterBulk
        });
        const { successItems, failedItems } = await bulk.run(items);
        return {
          total: items.length,
          processed: successItems.length + failedItems.length,
          successful: successItems.length,
          failed: failedItems.length
        };
      }
    });
    return true;
  };
}
